<template>
    <div class="flex w-full flex-col gap-4 rounded bg-white p-6 shadow">
        <div class="font-semibold">Subscription</div>

        <!-- if user has an active stripe subscription -->
        <div
            v-if="
                user.subscription &&
                user.subscription.stripe_subscription_id &&
                user.subscription.status == 'active'
            "
            class="flex flex-col gap-4"
        >
            <div
                class="flex items-start justify-between border-b border-gray-325 pb-4"
            >
                <div class="">Auto-Renew:</div>
                <div class="">
                    <span
                        v-if="user.subscription.renews_at"
                        class="rounded border border-green-325 p-1.5 py-1.5 text-center text-green-525"
                        >Active</span
                    >
                    <span
                        v-else
                        class="rounded border border-red-225 p-1.5 py-1.5 text-center text-red-525"
                        >Inactive</span
                    >
                </div>
            </div>
            <div
                v-if="user.subscription.renews_at"
                class="flex items-start justify-between border-b border-gray-325 pb-4"
            >
                <div class="">Next Payment:</div>
                <div class="">
                    <span>
                        {{
                            dayjs(user.subscription.renews_at)
                                .utc(true)
                                .tz($page.props.display_timezone)
                                .format($page.props.date_formats.short_nice)
                        }}
                        <span class="text-gray-625"
                            >(In
                            {{ user.subscription.days_left }}
                            days)</span
                        >
                    </span>
                </div>
            </div>
            <div
                v-else
                class="flex items-start justify-between border-b border-gray-325 pb-4"
            >
                <div class="">Expiration Date:</div>
                <div class="">
                    <span>
                        {{
                            dayjs(user.subscription.ends_at)
                                .utc(true)
                                .tz($page.props.display_timezone)
                                .format($page.props.date_formats.short_nice)
                        }}
                        <span class="text-gray-625"
                            >(In
                            {{ user.subscription.days_left }}
                            days)</span
                        >
                    </span>
                </div>
            </div>
            <div
                class="flex items-start justify-between border-b border-gray-325 pb-4"
            >
                <div class="">Monthly Payment:</div>
                <div class="">
                    <span>
                        ${{ user.subscription.plan.price_usd }} USD / month
                    </span>
                </div>
            </div>
            <div
                v-if="user.stripe_card_meta"
                class="flex flex-col items-start justify-between pb-4 md:flex-row"
            >
                <div class="">Card:</div>
                <div class="flex gap-2">
                    <div class="flex flex-col gap-0 md:flex-row md:gap-2">
                        <div>Ending in {{ user.stripe_card_meta.last_4 }}</div>
                        <div class="hidden flex-shrink md:block">·</div>
                        <div>
                            Expires
                            {{ user.stripe_card_meta.exp_month }}/{{
                                user.stripe_card_meta.exp_year
                            }}
                        </div>
                        <confirm-dialog
                            size="2xl"
                            confirm-button-label="Delete"
                            confirm-button-css="rounded border bg-red-400 px-3 py-2 text-white hover:cursor-pointer hover:bg-red-300"
                            cancel-button-label="Cancel"
                            cancel-button-css="rounded border border-gray-700 px-3 py-2 text-gray-700 hover:cursor-pointer hover:bg-gray-325"
                            v-on:confirm="deleteCardInfo"
                        >
                            <template #trigger>
                                <span
                                    class="text-red-500 hover:cursor-pointer hover:text-red-600"
                                    >Delete</span
                                >
                            </template>
                            <template #content>
                                <div class="flex flex-col gap-4">
                                    <div class="font-semibold">
                                        Delete Card?
                                    </div>
                                    <div class="">
                                        By deleting your payment card, you’ll
                                        also be turning off your auto-renew. Are
                                        you sure you want to do this?
                                    </div>
                                </div>
                            </template>
                        </confirm-dialog>
                    </div>
                </div>
            </div>
            <div v-else class="flex items-start justify-between pb-4">
                <div class="">Card:</div>
                <div class="">No card on file</div>
            </div>
            <div class="flex flex-col justify-end gap-2 md:flex-row">
                <div class="">
                    <StudentUpdatePaymentMethodModal />
                </div>

                <div
                    class="flex flex-col justify-end gap-2 md:flex-row"
                    v-if="user.stripe_card_meta"
                >
                    <div
                        v-if="user.subscription.renews_at"
                        @click="disableAutoRenew()"
                        class="rounded bg-primary-525 px-3 py-2 text-white hover:cursor-pointer"
                    >
                        Turn off auto-renew
                    </div>
                    <div
                        v-else
                        @click="enableAutoRenew()"
                        class="rounded bg-primary-525 px-3 py-2 text-white hover:cursor-pointer"
                    >
                        Turn on auto-renew
                    </div>
                </div>
            </div>
        </div>

        <!-- if user does not have an active stripe subscription -->
        <div v-else-if="user.subscription" class="flex flex-col gap-4">
            <div
                class="flex items-start justify-between border-b border-gray-325 pb-4"
            >
                <div class="">Expiration Date:</div>
                <div class="">
                    <span>
                        {{
                            dayjs(user.subscription.ends_at)
                                .utc(true)
                                .tz($page.props.display_timezone)
                                .format($page.props.date_formats.short_nice)
                        }}
                        <span class="text-gray-625"
                            >(In
                            {{ user.subscription.days_left }}
                            days)</span
                        >
                    </span>
                </div>
            </div>
            <div class="flex items-center justify-end">
                <span
                    v-if="user.subscription.status == 'active'"
                    class="rounded bg-primary-525 px-3 py-2 text-white opacity-50 hover:cursor-not-allowed"
                >
                    Add Subscription
                </span>
                <Link
                    v-else
                    :href="route('frontend.pricing')"
                    class="rounded bg-primary-525 px-3 py-2 text-white hover:cursor-pointer"
                >
                    Add Subscription
                </Link>
            </div>
        </div>
        <div v-else class="flex flex-col gap-4">
            <div
                class="flex items-start justify-between border-b border-gray-325 pb-4"
            >
                <div class="">You do not have an active subscription</div>
            </div>
            <div class="flex items-center justify-end">
                <Link
                    :href="route('frontend.pricing')"
                    class="rounded bg-primary-525 px-3 py-2 text-white hover:cursor-pointer"
                >
                    Add Subscription
                </Link>
            </div>
        </div>
    </div>
</template>
<script>
import StudentUpdatePaymentMethodModal from "@/Pages/NewStudentBackend/StudentUpdatePaymentMethodModal.vue";
import NewModal from "@/Components/NewModal.vue";
import ConfirmDialog from "@/Components/ConfirmDialog.vue";

export default {
    name: "StudentSubscriptionManageWidget",
    components: { ConfirmDialog, NewModal, StudentUpdatePaymentMethodModal },
    props: {
        user: {},
    },
    methods: {
        deleteCardInfo() {
            this.$inertia.get(route("remove-all-payment-methods"));
        },
        disableAutoRenew() {
            this.$inertia.get(
                route("student.account.subscription.disable-auto-renew")
            );
        },
        enableAutoRenew() {
            this.$inertia.get(
                route("student.account.subscription.enable-auto-renew")
            );
        },
    },
};
</script>
